import { sessionStore } from "csi/state/rootStore";
import { cnl } from "csi/typings";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ShareViewWith, ShareViewWithSubType } from "../options/CustomViewOptions";
import DefaultsConstructor, { Defaults } from "csi/prefs/Defaults";
import WorkingCache from "csi/state/WorkingCache";
import CustomViewSettingsStore from "./CustomViewSettingsStore";
import csi from "csi/main";
import csiPrefs from "csi/prefs";
import { uniq } from "lodash";
import { getDefaults } from "../customViewLeftPanel/customViewDialogLeftPanelService";

/**
 * return current user's playable roles
 */
export function getPlayableRoles(): ComponentItem[] {
	const { playableRolesStore } = sessionStore;
	const data = [];

	playableRolesStore.internalQuery().forEach((item) => {
		data.push({ id: item.$, name: item.Name });
	});

	return data;
}

/**
 * return current custom view share view with option
 */
export function getCustomViewSharedWith(prefIsShared: boolean, prefParty: cnl[]): ShareViewWith {
	const prefPartyLength = prefParty?.length;

	if (prefPartyLength > 0 && prefIsShared) {
		return ShareViewWith.ROLES;
	} else if (prefPartyLength === 1 && !prefIsShared) {
		return ShareViewWith.ME;
	} else if (!prefPartyLength && prefIsShared) {
		return ShareViewWith.EVERYONE;
	} else {
		return ShareViewWith.ME;
	}
}

/** initialize view settings in custom view dialog */
export function processViewSettings(
	settingStore: CustomViewSettingsStore,
	selectedCustomViewIsOOTB: boolean,
	selectedCustomViewCnl: cnl,
	customViewContext: string,
	isViewSharedWithEveryone: boolean,
	hasMultiple: boolean,
	defaults: Defaults,
	selectedViewIsSystemWideDefault: boolean,
	isViewSharedWithSpecificRolesOnly: boolean,
	partyCache: WorkingCache,
	specificSubTypeOptions: ComponentItem<any>[],
	playableRoles: ComponentItem<any>[],
	prefParty: string[],
): void {
	if (selectedCustomViewIsOOTB) {
		settingStore.everyoneSetViewAsDefault = true;
	} else if (selectedCustomViewCnl) {	
		const viewKey = csiPrefs.decomposeKey(customViewContext).view;
		// initialize share view with option "Everyone"
		if (isViewSharedWithEveryone) {
			if (hasMultiple) {
				// Check site default to see if any subtype defaults exist
				// handles share with specific subtypes in site default
				specificSubTypeOptions.forEach((subtype) => {
					const subtypeDefaultView = defaults.get(subtype.id, csi.EMPTY_URL, true);
					if (subtypeDefaultView === selectedCustomViewCnl) {
						settingStore.selectedEveryoneSpecificSubType.push(subtype);
						settingStore.everyoneSetViewAsDefault = true;
						settingStore.everyoneShareWithDefault = ShareViewWith.EVERYONE;
						settingStore.everyoneShareWithSubtypedDefault = ShareViewWithSubType.SPECIFIC;
					}
				});
			}
	
			// the view is shared system wide as the default view and current view is the default view
			if (selectedViewIsSystemWideDefault) {
				settingStore.everyoneSetViewAsDefault = true;
				settingStore.everyoneShareWithDefault = ShareViewWith.EVERYONE;
	
				// handles share with all subtypes in site defaults
				if (hasMultiple) {
					const systemWideDefaultView = defaults.get(viewKey, csi.EMPTY_URL, true);
					if (systemWideDefaultView === selectedCustomViewCnl) {
						settingStore.everyoneSetViewAsDefault = true;
						settingStore.selectedEveryoneSpecificSubType = [];
						settingStore.everyoneShareWithSubtypedDefault = ShareViewWithSubType.ALL;
					}
				}
			} else {
				// the view is shared system wide, but only set as default for specific roles
				playableRoles.forEach((role) => {
					if (hasMultiple) {
						const defaultViewForSpecificRoles = defaults.get(viewKey, role.id, true);
						// handles share with everyone but set view as default for specific roles, shared with all subtypes
						if (defaultViewForSpecificRoles === selectedCustomViewCnl) {
							settingStore.everyoneSetViewAsDefault = true;
							settingStore.selectedEveryoneSetViewDefaultSpecificRoles.push(role);
							settingStore.everyoneShareWithSubtypedDefault = ShareViewWithSubType.ALL;
							settingStore.everyoneShareWithDefault = ShareViewWith.ROLES;
						} else {
							// handles share with everyone but set view as default for specific roles, shared with specific subtypes
							specificSubTypeOptions.forEach((subtype) => {
								const subtypeDefaultView = defaults.get(subtype.id, role.id, true);
								if (subtypeDefaultView === selectedCustomViewCnl) {
									settingStore.everyoneSetViewAsDefault = true;
									settingStore.everyoneShareWithSubtypedDefault = ShareViewWithSubType.SPECIFIC;
									settingStore.selectedEveryoneSpecificSubType.push(subtype);
									settingStore.selectedEveryoneSetViewDefaultSpecificRoles.push(role);
									settingStore.everyoneShareWithDefault = ShareViewWith.ROLES;
								}
							});
							settingStore.selectedEveryoneSetViewDefaultSpecificRoles = uniq(settingStore.selectedEveryoneSetViewDefaultSpecificRoles);
							settingStore.selectedEveryoneSpecificSubType = uniq(settingStore.selectedEveryoneSpecificSubType);
						}
					} else {
						// handles non subtype views (regular views)
						const defaultViewForSpecificRoles = defaults.get(customViewContext, role.id, true);
						if (defaultViewForSpecificRoles === selectedCustomViewCnl) {
							settingStore.everyoneSetViewAsDefault = true;

							const specificRoleForSystemWideDefault = {
								disabled: !playableRoles.find((playRole) => playRole.id === role.id) || !csiPrefs.hasAdminAccess(),
								...role,
							};

							settingStore.selectedEveryoneSetViewDefaultSpecificRoles.push(specificRoleForSystemWideDefault);
							settingStore.everyoneShareWithDefault = ShareViewWith.ROLES;
						}
					}
				});
			}
		} else if (isViewSharedWithSpecificRolesOnly) {
			const roleDefault = getDefaults();
			prefParty.forEach((role) => {
				const rolePreferences = sessionStore.getRolePreferences(role);
				if (rolePreferences) {
					roleDefault.setRolePreferences(rolePreferences);
				}
			});
			if (hasMultiple) {
				prefParty.forEach((id) => {
					const defaultViewForRole = roleDefault.get(viewKey, id, true);
					const roleName = partyCache.get(id)?.$Name;
					// handles if role is sharing with all subtypes
					if (defaultViewForRole === selectedCustomViewCnl) {
						const role = {
							disabled: !playableRoles.find((playRole) => playRole.id === id),
							id,
							name: roleName,
						};
							
						settingStore.selectedSpecificRolesSetViewDefaultSpecificRoles.push(role);
						settingStore.specificRolesSetViewAsDefault = true;
					} else {
						// handles if roles is shared with specific subtypes 
						let isSharedWithRole = false;
						specificSubTypeOptions.forEach((subtype) => {
							const subtypeDefaultView = roleDefault.get(subtype.id, id, true);
							if (subtypeDefaultView === selectedCustomViewCnl) {
								settingStore.specificRolesSetViewAsDefault = true;
								settingStore.shareWithSubtypedDefault = ShareViewWithSubType.SPECIFIC;
								isSharedWithRole = true;
								settingStore.selectedSpecificSubType.push(subtype);
							}
						});
						if (isSharedWithRole) {
							const role = {
								disabled: !playableRoles.find((playRole) => playRole.id === id),
								id,
								name: roleName,
							};
							settingStore.selectedSpecificRolesSetViewDefaultSpecificRoles.push(role);
						}
						settingStore.selectedSpecificSubType = uniq(settingStore.selectedSpecificSubType);
					}
				});
			} else {
				// the view is shared with specific roles and set as default for some or all of them
				prefParty.forEach((id) => {
					const defaultViewForRole = roleDefault.get(customViewContext, id, true);
					if (defaultViewForRole === selectedCustomViewCnl) {
						const roleName = partyCache.get(id)?.$Name;
						const role = {
							disabled: !playableRoles.find((playRole) => playRole.id === id) || !csiPrefs.hasAdminAccess(),
							id,
							name: roleName,
						};
							
						settingStore.selectedSpecificRolesSetViewDefaultSpecificRoles.push(role);
						settingStore.specificRolesSetViewAsDefault = true;
					}
				});
			}
		}
		// the view is only shared with current user if none of the condition above applies
	}
}